import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Y, T, CREDIT, LABEL_FONT } from "../theme";
import { StatusIcon, RatioSymbol, ModelBadge, ModelIcon, ResPill, CreditIcon, downloadVideo, authDownloadAsJpg } from "./shared";
import { IcoRefresh, IcoDownload, IcoDelete, IcoLibrary, IcoGenerate, IcoWarning } from "../icons";
import { api, auth, isJobSentToProvider, JOB_SENT_TO_PROVIDER_HINT } from "../firebase";
import { useNotify, useHeaderRight, useConfirm, useNarrow } from "../App";
import { MiniCyberBars, ImagePreviewModal } from "./GenerateView";
import { useTheme } from "../useTheme";
import PageLoader from "./PageLoader";
import { KLING_EFFECTS } from "../kling-effects-registry";

const _libEffectByScene = new Map(KLING_EFFECTS.map(e => [e.scene, e]));
const libGetEffectLabel = (scene) => _libEffectByScene.get(scene)?.label || scene;

// ── Authenticated helpers (same pattern as GenerateView) ─────────────────────
const _blobCache = {};

function AuthImage({ src, style, alt = "", onClick, loadingIndicator = false }) {
  const [blobUrl, setBlobUrl] = useState(_blobCache[src] || null);
  useEffect(() => {
    if (_blobCache[src]) { setBlobUrl(_blobCache[src]); return; }
    let cancelled = false;
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          _blobCache[src] = url;
          setBlobUrl(url);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [src]);
  // While the authed blob is fetching, render a placeholder that occupies
  // the SAME box as the final image (style carries the reserved size) so
  // the layout doesn't jump and the slot never looks "missing". Mirrors
  // GenerateView's AuthImage so the loader reads identically app-wide.
  if (!blobUrl) {
    if (loadingIndicator) {
      return (
        <div style={{...style, background:T.media, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14}} onClick={onClick}>
          <MiniCyberBars/>
          <span style={{color:"rgba(255,255,255,0.7)", fontSize:11, fontFamily:'ui-monospace,Menlo,Consolas,monospace', letterSpacing:1.2, textTransform:'uppercase'}}>Loading image…</span>
        </div>
      );
    }
    return <div style={{...style, background:T.media, display:"flex", alignItems:"center", justifyContent:"center"}} onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
    </div>;
  }
  return <img src={blobUrl} alt={alt} style={{...style, cursor: onClick ? "zoom-in" : undefined}} onClick={onClick}/>;
}

// Parse a job's aspect ratio ("16:9", "1:1", "auto"/missing) into a numeric
// W/H so the detail-page output box can reserve the right space BEFORE the
// image bytes arrive. Unknown ratios fall back to 1 (square reservation);
// objectFit:contain then letterboxes the true image once it loads, so a
// wrong guess never crops - it just reserves slightly off.
function aspectRatioOf(job) {
  const ar = job?.aspectRatio;
  if (ar && ar !== "auto") {
    const m = String(ar).match(/^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/i);
    if (m) {
      const w = parseFloat(m[1]), h = parseFloat(m[2]);
      if (w > 0 && h > 0) return w / h;
    }
  }
  return 1;
}

function AuthVideo({ src, style }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const cacheBust = src + (src.includes('?') ? '&' : '?') + '_t=' + Date.now();
        const res = await fetch(cacheBust, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
        if (!res.ok || cancelled) { if (!cancelled) setError(true); return; }
        const data = await res.json();
        if (!cancelled) setVideoUrl(data.url);
      } catch { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [src]);
  if (error) return (
    <div style={{...style, background:T.dangerBg, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:4}}>
      <span style={{color:T.dangerText,fontSize:11}}>Failed to load video</span>
    </div>
  );
  if (!videoUrl) return (
    <div style={{...style, background:"rgba(255,255,255,0.05)", display:"flex", alignItems:"center", justifyContent:"center"}}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/></svg>
    </div>
  );
  return <video controls playsInline loop style={style} src={videoUrl}/>;
}

// Delegates to shared downloadVideo, which uses the Web Share API on iOS
// so users can tap "Save Video" -> Photos from the native share sheet.
async function authDownload(url, filename) {
  return downloadVideo(url, filename);
}

// Pick the right file extension for an image-job download. Reads the
// real extension off the saved GCS path (svg / png / jpg) and falls
// back to a model-id heuristic for jobs whose paths haven't been
// stamped yet. Mirrors imageJobExt in GenerateView.
function imageJobExt(job) {
  const path = (job?.imageOutputGcsPaths || [])[0] || '';
  const ext = (path.match(/\.([a-z0-9]+)$/i) || [])[1];
  if (ext) return ext.toLowerCase();
  const m = String(job?.model || '').toLowerCase();
  if (m === 'recraft-v4-vector' || m === 'recraft-v4-pro-vector' || m === 'recraft-v3-vector' || m === 'recraft-v3-vector-edit' || m === 'recraft-vectorize') return 'svg';
  return 'jpg';
}

// True when the output is worth offering a "Download as JPG" secondary
// button - i.e. anything not already JPG.
function jobOffersJpgConvert(job) {
  const ext = imageJobExt(job);
  return ext === 'svg' || ext === 'png';
}

// Image download — the output-images endpoint streams the image directly
// (not a signed-URL response), so we can fetch it and trigger a download.
async function authDownloadImage(url, filename) {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const token = await user.getIdToken();
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Image download failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error('Image download error:', err);
  }
}

// ── Helper functions ──────────────────────────────────────────────────────────
const supportsRatio = (id) => id && !id.startsWith('kling-3.');
const supportsCfg = (id) => id && id.startsWith('kling-1.');
const getResolution = (id) => id && id.includes('-pro') ? '1080p' : '720p';
// Match the image-context tokens used in GenerateView: ResPill maps
// "Img1K"/"Img2K"/"Img4K" to the image-side colors (blue/purple/gold)
// while displaying "1K"/"2K"/"4K". A plain "1K"/"2K"/"4K" key would
// pick up video colors, and now that video 4K renders as "2160p" the
// label would also be wrong.
const imgResLabel = (r) => r === '1k' ? 'Img1K' : r === '2k' ? 'Img2K' : r === '4k' ? 'Img4K' : (r || '').toString().toUpperCase();

function modeLabel(inputMode) {
  switch (inputMode) {
    case 'image2video': return { short: 'I → V', long: 'Image → Video' };
    case 'ref2video':   return { short: 'R → V', long: 'References → Video' };
    case 'text2video':  return { short: 'T → V', long: 'Text → Video' };
    case 'ref2image':   return { short: 'R → I', long: 'References → Image' };
    case 'text2image':  return { short: 'T → I', long: 'Text → Image' };
    default: return null;
  }
}

// ── Icon labels for detail page ──────────────────────────────────────────────
const I = {
  id:       <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
  model:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h6v6H9z"/></svg>,
  dur:      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="13" r="8"/><polyline points="12 9 12 13 14.5 15.5"/></svg>,
  ratio:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
  audio:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  cfg:      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/></svg>,
  cost:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  created:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  completed:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  task:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  res:      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
};

// ── Full-page detail view ────────────────────────────────────────────────────
function DetailPage({ job, onBack, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState(null);
  const setHeaderRight = useHeaderRight();
  const navigate = useNavigate();
  // Theme subscription so header buttons re-render on light/dark toggle
  // (otherwise T.* tokens captured into the JSX tree go stale).
  const { mode: themeMode } = useTheme();
  const statusColor = { ready:Y, processing:"#5BBFB8", queued:"#415459", failed:T.danger };
  const sc = statusColor[job.status] || T.text3;
  const isImage = job.outputType === 'image';
  const outs = job.imageOutputGcsPaths || [];
  // Outputs the model refused (moderation). Rendered as placeholder slots
  // after the real images so an n-output request always shows n boxes.
  const blockedOut = job.blockedOutputs || 0;
  const totalSlots = outs.length + blockedOut;

  const handleDel = async () => {
    setDeleting(true);
    // Un-dim if the delete is declined in the dialog or refused (a job already
    // sent to the provider can't be cancelled); a successful one navigates away.
    try { await onDelete(job.id); } finally { setDeleting(false); }
  };

  // Cross-page reuse: hand the job to GenerateView via router state. The
  // target sub-tab matches the job's inputMode so the form already shows
  // the right zones (img2vid frame slot vs ref2vid pickers vs txt2vid
  // prompt-only) when the prefilled fields land.
  const handleReuse = () => {
    // References is the new default video input; only an explicit
    // image2video job lands on Start / End Frame. text2video re-derives
    // at submit from the empty drop, so it can land on References too.
    let target = '/generate/video/ref2vid';
    if (isImage) {
      target = '/generate/image';
    } else if (job.inputMode === 'image2video') {
      target = '/generate/video/img2vid';
    }
    navigate(target, { state: { reuseJob: job } });
  };

  const downloadAllImages = async () => {
    const ext = imageJobExt(job);
    for (let i = 0; i < outs.length; i++) {
      await authDownloadImage(api.getOutputImageUrl(job.id, i), `katana-ai-${job.id}-${i+1}.${ext}`);
    }
  };
  const downloadAllImagesAsJpg = async () => {
    for (let i = 0; i < outs.length; i++) {
      await authDownloadAsJpg(api.getOutputImageUrl(job.id, i), `katana-ai-${job.id}-${i+1}.jpg`);
    }
  };

  // Register back + download + delete buttons in page header
  useEffect(() => {
    setHeaderRight(
      <div style={{display:"flex",alignItems:"center",gap:8,width:"100%"}}>
        <button onClick={onBack} title="Back to library" style={{background:"rgba(255,255,255,0.05)",border:"none",color:T.text2,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,width:32,height:32,borderRadius:10,transition:"background 0.15s",marginRight:"auto"}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        {!isImage && job.status === "ready" && job.gcsPath && (
          <button onClick={() => authDownload(api.getVideoUrl(job.id), `katana-ai-${job.id}.mp4`)} title="Download"
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
            <IcoDownload s={14} c={Y}/>
          </button>
        )}
        {isImage && job.status === "ready" && outs.length > 0 && jobOffersJpgConvert(job) && (
          <button onClick={downloadAllImagesAsJpg} title={outs.length === 1 ? "Download as JPG" : `Download all ${outs.length} images as JPG`}
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,fontFamily:"Mulish,sans-serif",letterSpacing:0.5}}>
            <span>JPG</span>
            <IcoDownload s={14} c={Y}/>
          </button>
        )}
        {isImage && job.status === "ready" && outs.length > 0 && (
          <button onClick={downloadAllImages} title={outs.length === 1 ? `Download as ${imageJobExt(job).toUpperCase()}` : `Download all ${outs.length} images as ${imageJobExt(job).toUpperCase()}`}
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
            <IcoDownload s={14} c={Y}/>
            {outs.length > 1 && <span>×{outs.length}</span>}
          </button>
        )}
        <button onClick={handleReuse} title="Reuse settings on Generate page"
          style={{padding:"8px 14px",borderRadius:10,background:`${Y}20`,color:Y,border:`1px solid ${Y}66`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,fontFamily:"Mulish,sans-serif",letterSpacing:0.5}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
          <span style={{fontFamily:LABEL_FONT,letterSpacing:0.5,textTransform:"uppercase"}}>Reuse</span>
        </button>
        <button onClick={handleDel} title={isJobSentToProvider(job) ? JOB_SENT_TO_PROVIDER_HINT : job.status === "queued" ? "Cancel job" : "Delete"}
          style={{padding:"8px 14px",borderRadius:10,background:T.dangerBg,color:T.danger,border:`1px solid ${T.dangerBorder}`,cursor:isJobSentToProvider(job)?"not-allowed":"pointer",opacity:isJobSentToProvider(job)?0.4:1,display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
          <IcoDelete s={14} c={T.danger}/>
        </button>
      </div>
    );
    return () => setHeaderRight(null);
  }, [job.id, job.status, isImage, outs.length, themeMode]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20,opacity:deleting?0.4:1,transition:"opacity 0.2s"}}>

      {/* Status + Model header */}
      <div style={{display:"flex",alignItems:"flex-end",gap:12}}>
        <div style={{color:sc,display:"flex",alignSelf:"flex-end",marginBottom:2}}>
          {job.status === "ready" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
          {job.status === "processing" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/></svg>}
          {job.status === "queued" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          {job.status === "failed" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
        </div>
        <div style={{marginBottom:-4}}><ModelIcon model={job.model} s={40}/></div>
        <div style={{display:"flex",alignItems:"baseline",gap:10}}>
          <span style={{color:T.title,fontSize:16,fontWeight:700}}>{job.model}</span>
          {job.cost > 0 && <span style={{display:"inline-flex",alignItems:"center",gap:2,color:CREDIT,fontSize:14,fontWeight:700}}><CreditIcon size={12} color={CREDIT}/>{Math.round(job.cost / 0.002)}</span>}
        </div>
      </div>

      {/* Output + Source row */}
      <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"flex-start"}}>
        {/* Image-gen outputs gallery. Each output reserves its box via
            aspectRatio so the slot never looks "missing" while the authed
            blob loads (a loader shows in-place), scales to the viewport
            width on mobile instead of overflowing, and opens the shared
            fullscreen lightbox on tap - same viewer the job cards use. */}
        {isImage && job.status === "ready" && outs.length > 0 && (
          <div style={{display:"flex",gap:8,flexWrap:"wrap",width:"100%",maxWidth:560}}>
            {outs.map((_, idx) => (
              <div key={idx} onClick={() => setPreview(idx)} title="View fullscreen"
                style={{position:"relative",width:totalSlots>1?"calc(50% - 4px)":"100%",maxWidth:560,aspectRatio:String(aspectRatioOf(job)),background:"#000",borderRadius:4,overflow:"hidden",cursor:"zoom-in"}}>
                <AuthImage src={api.getOutputImageUrl(job.id, idx)} alt={`output ${idx+1}`} loadingIndicator
                  style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/>
                {jobOffersJpgConvert(job) && (
                  <button onClick={(e) => {e.stopPropagation();authDownloadAsJpg(api.getOutputImageUrl(job.id, idx), `katana-ai-${job.id}-${idx+1}.jpg`);}}
                    title="Download this image as JPG"
                    style={{position:"absolute",bottom:8,right:64,padding:"6px 10px",borderRadius:8,background:"rgba(12,14,15,0.85)",color:Y,border:`1px solid ${Y}44`,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:10,fontWeight:700,backdropFilter:"blur(8px)",fontFamily:"Mulish,sans-serif",letterSpacing:0.5}}>
                    <span>JPG</span>
                    <IcoDownload s={12} c={Y}/>
                  </button>
                )}
                <button onClick={(e) => {e.stopPropagation();authDownloadImage(api.getOutputImageUrl(job.id, idx), `katana-ai-${job.id}-${idx+1}.${imageJobExt(job)}`);}}
                  title={`Download this image as ${imageJobExt(job).toUpperCase()}`}
                  style={{position:"absolute",bottom:8,right:8,padding:"6px 10px",borderRadius:8,background:"rgba(12,14,15,0.85)",color:Y,border:`1px solid ${Y}44`,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:10,fontWeight:600,backdropFilter:"blur(8px)"}}>
                  <IcoDownload s={12} c={Y}/>
                  {outs.length > 1 && <span>{idx+1}</span>}
                </button>
              </div>
            ))}
            {/* Moderation-blocked outputs: one placeholder per refused slot so
                an n-output request still shows n boxes. */}
            {Array.from({ length: blockedOut }).map((_, bi) => (
              <div key={`blocked-${bi}`} title="This output was blocked by content moderation"
                style={{position:"relative",width:totalSlots>1?"calc(50% - 4px)":"100%",maxWidth:560,aspectRatio:String(aspectRatioOf(job)),background:T.media,border:`1px dashed ${T.danger}55`,borderRadius:4,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,padding:12,boxSizing:"border-box",textAlign:"center"}}>
                <IcoWarning s={30} c={T.danger}/>
                <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.danger}}>Blocked by moderation</span>
              </div>
            ))}
          </div>
        )}
        {/* Video player */}
        {!isImage && job.status === "ready" && job.gcsPath && (
          <AuthVideo src={api.getVideoUrl(job.id)}
            style={{width:"100%",maxWidth:560,borderRadius:4,background:"#000"}}/>
        )}
        {/* Source image (start frame for video, or absent for image-gen) */}
        {job.imageGcsPath && !isImage && (
          <div style={{flex:"0 0 auto"}}>
            <AuthImage src={api.getImageUrl(job.id, { thumb: true })} alt="Source"
              style={{width:100,height:100,borderRadius:4,objectFit:"cover"}}/>
            <div style={{color:T.text3,fontSize:10,marginTop:4,textAlign:"center"}}>Source</div>
          </div>
        )}
        {/* Reference images */}
        {(job.referenceImageGcsPaths || []).length > 0 && (
          <div style={{flex:"0 0 auto",display:"flex",flexDirection:"column",gap:4}}>
            <div style={{color:T.text3,fontSize:10,letterSpacing:0.5,textTransform:"uppercase"}}>References</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",maxWidth:220}}>
              {(job.referenceImageGcsPaths || []).map((_, idx) => (
                <AuthImage key={idx} src={api.getReferenceImageUrl(job.id, idx, { thumb: true })} alt={`ref ${idx+1}`}
                  style={{width:72,height:72,borderRadius:4,objectFit:"cover"}}/>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Metadata grid */}
      <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"8px 12px",fontSize:12,alignItems:"center",maxWidth:400}}>
        <span style={{color:T.text3,display:"flex"}} title="Job ID">{I.id}</span>
        <span style={{color:T.text2,fontFamily:"monospace",fontSize:10}}>{job.id}</span>

        <span style={{color:T.text3,display:"flex"}} title="Model">{I.model}</span>
        <span style={{color:T.text2,display:"flex",alignItems:"center",gap:6}}><ModelIcon model={job.model} s={14}/>{job.model}</span>

        {job.inputMode && (() => { const ml = modeLabel(job.inputMode); return ml ? (
          <React.Fragment>
            <span style={{color:T.text3,display:"flex"}} title="Input Mode"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h18"/><polyline points="13 5 21 12 13 19"/></svg></span>
            <span style={{color:T.text2}}>{ml.long}</span>
          </React.Fragment>
        ) : null; })()}

        <span style={{color:T.text3,display:"flex"}} title="Resolution">{I.res}</span>
        <span style={{justifySelf:"start"}}><ResPill res={isImage ? imgResLabel(job.imageResolution || '1k') : getResolution(job.model)}/></span>

        {!isImage && <>
          <span style={{color:T.text3,display:"flex"}} title="Duration">{I.dur}</span>
          <span style={{color:T.title}}>{job.duration}s</span>
        </>}

        {isImage && <>
          <span style={{color:T.text3,display:"flex"}} title="Outputs"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10"/></svg></span>
          <span style={{color:T.title}}>×{outs.length || job.n || 1}</span>
        </>}

        <span style={{color:T.text3,display:"flex"}} title="Aspect Ratio">{I.ratio}</span>
        <span style={{color:T.text2}}>{(!job.aspectRatio || job.aspectRatio === "auto" || (!isImage && !supportsRatio(job.model))) ? "Auto" : job.aspectRatio}</span>

        {!isImage && <>
          <span style={{color:T.text3,display:"flex"}} title="Audio">{I.audio}</span>
          <span style={{color:T.text2}}>{job.audio ? "Yes" : "No"}</span>
        </>}

        {!isImage && supportsCfg(job.model) && job.cfgScale !== undefined && <>
          <span style={{color:T.text3,display:"flex"}} title="CFG Scale">{I.cfg}</span>
          <span style={{color:T.text2}}>{job.cfgScale}</span>
        </>}

        {job.cost > 0 && <>
          <span style={{color:T.text3,display:"flex"}} title="Credits">{I.cost}</span>
          <span style={{display:"inline-flex",alignItems:"center",gap:2,color:CREDIT,fontWeight:700}}><CreditIcon size={11} color={CREDIT}/>{Math.round(job.cost / 0.002)}</span>
        </>}

        <span style={{color:T.text3,display:"flex"}} title="Created">{I.created}</span>
        <span style={{color:T.text2}}>{job.createdAt ? new Date(job.createdAt).toLocaleString() : "—"}</span>

        {job.completedAt && <>
          <span style={{color:T.text3,display:"flex"}} title="Completed">{I.completed}</span>
          <span style={{color:T.text2}}>{new Date(job.completedAt).toLocaleString()}</span>
        </>}

        {job.klingTaskId && <>
          <span style={{color:T.text3,display:"flex"}} title="Kling Task ID">{I.task}</span>
          <span style={{color:T.text2,fontFamily:"monospace",fontSize:10}}>{job.klingTaskId}</span>
        </>}
      </div>

      {/* Kling Effect (effects jobs have no prompt; surface the scene
          label + key in the same slot the prompt would normally occupy). */}
      {job.model === 'kling-effects' && job.klingEffectScene && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:6,color:T.text3}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            <span style={{fontSize:10,fontWeight:600}}>Effect</span>
          </div>
          <div style={{color:T.text2,fontSize:13,lineHeight:1.6,padding:"10px 14px",background:"rgba(255,255,255,0.03)",borderRadius:10,display:"flex",alignItems:"center",gap:8}}>
            <span style={{color:T.title,fontWeight:700}}>{libGetEffectLabel(job.klingEffectScene)}</span>
            <span style={{color:T.text3,fontFamily:"monospace",fontSize:11}}>{job.klingEffectScene}</span>
          </div>
        </div>
      )}

      {/* Prompt */}
      {job.prompt && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:6,color:T.text3}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            <span style={{fontSize:10,fontWeight:600}}>Prompt</span>
          </div>
          <div style={{color:T.text2,fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap",padding:"10px 14px",background:"rgba(255,255,255,0.03)",borderRadius:10}}>{job.prompt}</div>
        </div>
      )}

      {/* Negative prompt */}
      {job.negativePrompt && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:6,color:T.text3}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            <span style={{fontSize:10,fontWeight:600}}>Negative</span>
          </div>
          <div style={{color:T.text2,fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap",padding:"10px 14px",background:"rgba(255,255,255,0.03)",borderRadius:10}}>{job.negativePrompt}</div>
        </div>
      )}

      {/* Error */}
      {job.status === "failed" && job.error && (
        <div style={{padding:"12px 14px",borderRadius:10,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,display:"flex",alignItems:"flex-start",gap:8}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:2}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div style={{color:T.dangerText,fontSize:12,lineHeight:1.5,fontFamily:"monospace",whiteSpace:"pre-wrap"}}>{job.error}</div>
        </div>
      )}

      {/* Bottom action bar */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:12,paddingTop:16,borderTop:"1px solid rgba(255,255,255,0.04)"}}>
        <button onClick={onBack} title="Back to library" style={{background:"rgba(255,255,255,0.05)",border:"none",color:T.text2,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,width:36,height:36,borderRadius:10,transition:"background 0.15s"}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{flex:1}}/>
        {!isImage && job.status === "ready" && job.gcsPath && (
          <button onClick={() => authDownload(api.getVideoUrl(job.id), `katana-ai-${job.id}.mp4`)} title="Download"
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
            <IcoDownload s={14} c={Y}/>
          </button>
        )}
        {isImage && job.status === "ready" && outs.length > 0 && jobOffersJpgConvert(job) && (
          <button onClick={downloadAllImagesAsJpg} title={outs.length === 1 ? "Download as JPG" : `Download all ${outs.length} images as JPG`}
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,fontFamily:"Mulish,sans-serif",letterSpacing:0.5}}>
            <span>JPG</span>
            <IcoDownload s={14} c={Y}/>
          </button>
        )}
        {isImage && job.status === "ready" && outs.length > 0 && (
          <button onClick={downloadAllImages} title={outs.length === 1 ? `Download as ${imageJobExt(job).toUpperCase()}` : `Download all ${outs.length} images as ${imageJobExt(job).toUpperCase()}`}
            style={{padding:"8px 14px",borderRadius:10,background:`${Y}1A`,color:Y,border:`1px solid ${Y}33`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
            <IcoDownload s={14} c={Y}/>
            {outs.length > 1 && <span>×{outs.length}</span>}
          </button>
        )}
        <button onClick={handleDel} title={isJobSentToProvider(job) ? JOB_SENT_TO_PROVIDER_HINT : job.status === "queued" ? "Cancel job" : "Delete"}
          style={{padding:"8px 14px",borderRadius:10,background:T.dangerBg,color:T.danger,border:`1px solid ${T.dangerBorder}`,cursor:isJobSentToProvider(job)?"not-allowed":"pointer",opacity:isJobSentToProvider(job)?0.4:1,display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600}}>
          <IcoDelete s={14} c={T.danger}/>
        </button>
      </div>

      {/* Shared fullscreen lightbox (same component the job cards use):
          portals to body at z-9999 so it sits above the detail overlay. */}
      {preview !== null && outs.length > 0 && (
        <ImagePreviewModal
          jobId={job.id}
          model={job.model}
          outputExt={imageJobExt(job)}
          index={preview}
          count={outs.length}
          onClose={() => setPreview(null)}
          onIndexChange={(i) => setPreview(i)}
        />
      )}

    </div>
  );
}

// ── Main Library View ────────────────────────────────────────────────────────
export default function LibraryView() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [jobs,     setJobs]     = useState([]);
  const [kind,     setKind]     = useState(() => {
    try { return localStorage.getItem('katana.libraryKind') === 'image' ? 'image' : 'video'; }
    catch { return 'video'; }
  });
  useEffect(() => { try { localStorage.setItem('katana.libraryKind', kind); } catch {} }, [kind]);
  const [loading,  setLoading]  = useState(true);
  const [deleting, setDeleting] = useState(null);
  const { notify } = useNotify();
  const setError = (msg) => msg ? notify("error", msg, 6000) : null;
  const setHeaderRight = useHeaderRight();
  const confirm = useConfirm();
  // Theme subscription so the header (kind tabs, count, refresh) re-renders on toggle.
  const { mode: themeMode } = useTheme();

  // Derive selected job from URL param
  const sel = jobId ? jobs.find(j => j.id === jobId) || null : null;

  const narrow = useNarrow();
  // The detail view renders as an OVERLAY on top of the still-mounted list
  // (see the bottom of the render) rather than replacing it, so going back
  // never refetches the list or loses scroll position. This ref + effect
  // resets the overlay's own scroll to the top each time it opens / the
  // selected job changes (the list behind it keeps its position).
  const detailScrollRef = useRef(null);
  useEffect(() => {
    if (sel && detailScrollRef.current) detailScrollRef.current.scrollTop = 0;
  }, [sel && sel.id]);

  const loadJobs = useCallback(() => {
    setLoading(true);
    setError(null);
    api.listJobs(undefined, undefined, kind)
      .then(data => { setJobs(data.jobs || []); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [kind]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleDelete = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (isJobSentToProvider(job)) { setError(JOB_SENT_TO_PROVIDER_HINT); return; }
    const isImage = job?.outputType === 'image';
    // The confirm dialog's <img> can't send auth headers, so we fetch the
    // representative thumbnail bytes and hand it a blob URL instead. Skip
    // the preview entirely if no thumbnail is available or the fetch fails.
    let previewUrl = null;
    try {
      let endpoint = null;
      if (isImage && (job?.imageOutputGcsPaths || []).length > 0) endpoint = api.getOutputImageUrl(jobId, 0, { thumb: true });
      else if (job?.imageGcsPath)                                  endpoint = api.getImageUrl(jobId, { thumb: true });
      else if (job?.posterGcsPath)                                 endpoint = api.getPosterUrl(jobId, { thumb: true });
      if (endpoint) {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const blob = await res.blob();
            previewUrl = URL.createObjectURL(blob);
          }
        }
      }
    } catch {}
    // Still waiting to be sent, so cancelling costs nothing.
    const cancelling = job?.status === 'queued';
    const ok = await confirm({
      title: cancelling
        ? (isImage ? 'Cancel this image job?' : 'Cancel this video job?')
        : (isImage ? 'Delete this image job?' : 'Delete this video job?'),
      message: cancelling
        ? "It hasn't been sent to the provider yet, so you won't be charged. The job is removed from your library."
        : 'This permanently removes the job and any generated outputs from your library. Credits already charged are not refunded.',
      imageUrl: previewUrl,
      confirmLabel: cancelling ? 'Cancel job' : 'Delete',
      danger: true,
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (!ok) return;

    setDeleting(jobId);
    try {
      await api.deleteJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      navigate("/library", { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(null);
    }
  };


  // Register the page header: Videos / Images kind-tabs on the LEFT
  // (styled identically to GenerateView's Video / Image mode tabs, same
  // T.title colors, yellow active underline, brand IcoGenerate for the
  // video tab and the photo-frame glyph for image), and the existing
  // status-filter pills + count + refresh on the RIGHT. The kind tabs
  // used to be inline at the top of the body; moving them to the
  // header matches the Generate page and frees the body for content.
  const KindTabIcon = ({ id }) => id === 'image' ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
  ) : (
    <span style={{display:'inline-flex',alignItems:'center'}}><IcoGenerate s={22} c="currentColor" sw={40}/></span>
  );
  useEffect(() => {
    if (sel) return; // DetailPage manages its own header
    setHeaderRight(
      <div style={{display:'flex',flex:1,alignItems:'center',justifyContent:'space-between',width:'100%',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          {[{id:'video',label:'Videos'},{id:'image',label:'Images'}].map(t => (
            <button key={t.id} onClick={()=>setKind(t.id)}
              title={t.label}
              style={{
                background:'transparent',border:'none',padding:'8px 16px',cursor:'pointer',
                borderBottom:`2px solid ${kind===t.id?Y:'transparent'}`,
                color: kind===t.id ? T.title : `${T.title}99`,
                fontSize:13,fontWeight:kind===t.id?700:500,
                fontFamily:LABEL_FONT,letterSpacing:0.5,textTransform:'uppercase',
                transition:'color 0.15s, border-color 0.15s',
                display:'inline-flex',alignItems:'center',gap:8,whiteSpace:'nowrap',
              }}
              onMouseEnter={e=>{ if(kind!==t.id) e.currentTarget.style.color = T.title; }}
              onMouseLeave={e=>{ if(kind!==t.id) e.currentTarget.style.color = `${T.title}99`; }}>
              <KindTabIcon id={t.id}/>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0,paddingRight:4}}>
          <div style={{display:'flex',alignItems:'center',gap:4,marginRight:8}}>
            <IcoLibrary s={13} c={T.text3}/>
            <span style={{color:T.text3,fontSize:11,fontWeight:700,fontFamily:'monospace'}}>{jobs.length}</span>
          </div>
          <button onClick={loadJobs} title="Refresh"
            style={{width:28,height:28,borderRadius:20,background:'transparent',border:'none',color:T.text3,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <IcoRefresh s={14} c={T.text3}/>
          </button>
        </div>
      </div>
    );
    return () => setHeaderRight(null);
  }, [jobs.length, sel, themeMode, kind]); // eslint-disable-line

  // Detail is no longer an early return that swaps out the list - it is an
  // overlay rendered at the very end of this component so the list stays
  // mounted and keeps its scroll. The two guards below still apply only to
  // a cold deep-link to /library/:jobId (there is no list to preserve yet).
  // If jobId is in URL but job not found yet (still loading), show loader
  if (jobId && !sel && loading) {
    return <PageLoader ready={false}/>;
  }
  // If jobId in URL but not found after loading, redirect to library
  if (jobId && !sel && !loading) {
    navigate("/library", { replace: true });
  }

  return (
    <div>
      <PageLoader ready={!loading}/>

      {/* Videos / Images kind tabs live in the page header now. */}

      {loading ? null : jobs.length === 0 ? (
        <div style={{color:T.text3,fontSize:12,padding:"20px 0"}}>
          {`No ${kind === 'image' ? 'images' : 'videos'} yet. Generate one to get started!`}
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
          {jobs.map(job => {
            const isImg = job.outputType === 'image';
            const outs  = job.imageOutputGcsPaths || [];
            return (
            <div key={job.id} onClick={()=>navigate(`/library/${job.id}`)}
              style={{borderRadius:4,overflow:"hidden",cursor:"pointer",transition:"all 0.15s",background:T.media,opacity:deleting===job.id?0.4:1}}>

              {/* Thumbnail */}
              <div style={{aspectRatio:"1/1",background:T.media,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                {/* Image-gen output as thumbnail (object-fit: contain so the
                    aspect ratio is visible, mirroring how videos render). */}
                {isImg && job.status === "ready" && outs.length > 0 && (
                  <AuthImage src={api.getOutputImageUrl(job.id, 0, { thumb: true })} alt=""
                    style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain"}}/>
                )}
                {/* Video-gen source frame thumbnail. Was opacity:0.4 (a
                    "ghost" look so the StatusIcon could pop on top), then
                    0.9 - both still read as washed-out. Now full opacity;
                    the StatusIcon is layered on top via z-order so it
                    stays visible without dimming the thumbnail. Failed
                    jobs are dimmed back to 0.35 so the red error
                    triangle reads clearly against the source frame. */}
                {!isImg && job.imageGcsPath && (
                  <AuthImage src={api.getImageUrl(job.id, { thumb: true })} alt=""
                    style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",opacity:job.status==="failed"?0.35:1}}/>
                )}
                {/* Video-gen jobs without a source frame (ref2video /
                    text2video): fall back to the server-extracted poster
                    so they get a real thumbnail too. */}
                {!isImg && !job.imageGcsPath && job.posterGcsPath && job.status === 'ready' && (
                  <AuthImage src={api.getPosterUrl(job.id, { thumb: true })} alt=""
                    style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain"}}/>
                )}
                <StatusIcon status={job.status} size={job.status==="processing"?20:24}/>
                <div style={{position:"absolute",bottom:6,left:6,right:6,display:"flex",alignItems:"center",gap:6}}>
                  <ModelIcon model={job.model} s={22}/>
                  {job.status !== "failed" && (
                    <ResPill res={isImg ? imgResLabel(job.imageResolution || '1k') : getResolution(job.model)} small/>
                  )}
                  {job.status !== "failed" && !isImg && <span style={{color:T.title,fontSize:10,fontWeight:700}}>{job.duration}s</span>}
                  {job.status !== "failed" && isImg && outs.length > 1 && <span style={{color:T.title,fontSize:10,fontWeight:700}}>×{outs.length}</span>}
                </div>
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Detail overlay. Geometry mirrors the App shell scroller (App.js:
          nav width 4em, header offset 84/116, content padding + CONTENT_MAX
          960) so it reads exactly like a normal page - but because it is an
          overlay, the library list underneath stays mounted, so closing it
          (back) restores the user's exact scroll position with no refetch.
          z-40 keeps it below the fixed header (z-50) so DetailPage's
          header-injected back/download/delete buttons stay clickable. */}
      {sel && (
        <div ref={detailScrollRef} style={{
          position:"fixed",
          top:narrow?116:84,
          left:narrow?0:"4em",
          right:0,bottom:0,
          zIndex:40,
          background:T.bg,
          overflowY:"auto",overflowX:"hidden",
        }}>
          <div style={{padding:narrow?"20px 12px 2em":"20px 1.75em 3em"}}>
            <div style={narrow?undefined:{maxWidth:960,margin:"0 auto"}}>
              <DetailPage job={sel} onBack={() => navigate("/library")} onDelete={handleDelete}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
